/* eslint-disable no-underscore-dangle */
import React, { useState, useContext, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { cloneDeep, debounce } from 'lodash';
import PropTypes from 'prop-types';

import { formatNumberInputValue, getEmptyTableFields, restrictNonNumericCharacters } from '../../../../shared/utils';
import { whenSideBySide, assignFormDesignToCSSVars } from '../fields-customise.style';
import SpecialPropsIndicators from '../props-Indicator/special-props';
import { controlValidate, getStateValue } from '../../../form/utils';
import { FormContext } from '../../../shared/contexts/form-context';
import { setUserInputOnCtrl } from '../../../form/reducer/form';
import { ctrlDefaults } from '../../../../shared/form-lib';
import { selectCtrlIdLabelMap } from '../../selectors';
import { ControlPropType } from '../../ctrl_proptype';
import {
  ValidationWarning,
  MainfieldWrapper,
  SubtextWrapper,
  FieldWrapper,
  LabelWrapper,
  InputWrapper,
  Input,
  Field,
} from '../../common-field-styles';

const TextInput = ({ ctrl, inTable, col, tr, formDesign }) => {
  const { inCustomise, inBuilder, currentPageNo } = useContext(FormContext);
  const { userCustomTexts, form } = useSelector((state) => state.form);
  const responseStore = form?.response_store ?? 'formplus';
  const ctrlIdLabelMap = useSelector(selectCtrlIdLabelMap);
  const fieldLabel = ctrlIdLabelMap?.[ctrl.internal_id];
  const dispatch = useDispatch();

  const [newCtrl, setNewCtrl] = useState(ctrl);
  const [inputValue, setInputValue] = useState(getStateValue(ctrl.value, col, tr, inTable));
  const [ctrlValidationError, setCtrlValidationError] = useState(
    getStateValue(ctrl._validationError, col, tr, inTable, inBuilder)
  );

  useEffect(() => {
    setInputValue(getStateValue(ctrl.value, col, tr, inTable));
  }, [ctrl.value, inTable, col, tr]);

  useEffect(() => {
    setCtrlValidationError(getStateValue(ctrl._validationError, col, tr, inTable, inBuilder));
  }, [ctrl._validationError, inTable, col, tr]);

  const saveUpdatedCtrl = useRef(
    debounce((newValue) => {
      dispatch(
        setUserInputOnCtrl({
          newValue,
          property: 'value',
          page: currentPageNo - 1,
          ctrlId: ctrl.internal_id,
          ...(inTable && { tr: tr - 1, col: col - 1 }),
        })
      );
    }, 200)
  );

  const handleInputValue = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    if (inTable) {
      const control = cloneDeep(newCtrl);
      if (!newCtrl.value) {
        control.value = getEmptyTableFields(newCtrl.props.min_rows, newCtrl.props.columns);
        control._validationError = getEmptyTableFields(newCtrl.props.min_rows, newCtrl.props.columns);
      }
      control.value[tr - 1][col - 1] = newValue;
      setNewCtrl(control);
    } else {
      setNewCtrl((prevCtrl) => ({ ...cloneDeep(prevCtrl), value: newValue }));
    }
  };

  const handleBlur = () => {
    controlValidate({
      isPayOnSubmission: false,
      setCtrlValidationError,
      userCustomTexts,
      responseStore,
      ctrl: newCtrl,
      setNewCtrl,
      col,
      tr
    });
  };

  const handleChange = (e) => {
    handleInputValue(e);
    const newValue = e.target.value;
    if (inTable || (!inTable && ctrl.value !== newValue)) {
      saveUpdatedCtrl.current(newValue);
    }
  };

  const handlePaste = (e) => formatNumberInputValue(e, handleChange);

  return (
    <MainfieldWrapper style={assignFormDesignToCSSVars(inBuilder, formDesign)}>
      <FieldWrapper>
        {ctrlValidationError && (
          <ValidationWarning className="ui bottom pointing basic label red">{ctrlValidationError}</ValidationWarning>
        )}

        <Field className={!inBuilder && !inTable ? whenSideBySide(ctrl, formDesign) : ''}>
          {!inTable && (
            <LabelWrapper
              notInBuilder={!inBuilder}
              required={ctrl.props.required}
              className={!inBuilder && !inTable ? whenSideBySide(ctrl, formDesign) : ''}>
              <label
                htmlFor={`fpl-${ctrl.internal_id}`}
                dangerouslySetInnerHTML={{ __html: fieldLabel || ctrlDefaults()[ctrl.type] }}
              />
              <SpecialPropsIndicators ctrl={ctrl} />
            </LabelWrapper>
          )}
          <InputWrapper className={!inBuilder && !inTable ? whenSideBySide(ctrl, formDesign) : ''}>
            <Input
              value={inputValue}
              onBlur={handleBlur}
              onPaste={handlePaste}
              onChange={handleChange}
              notInBuilder={!inBuilder}
              aria-describedby="descriptive text"
              onKeyDown={restrictNonNumericCharacters}
              type={ctrl.props.type === 2 ? 'number' : 'text'}
              inputMode={ctrl.props.type === 2 ? 'numeric' : 'text'}
              name={inTable ? `${ctrl.internal_id}-${tr}-${col}` : ''}
              disabled={ctrl.props.readonly || inCustomise || inBuilder}
              placeholder={ctrl.props.placeholder || 'Type your answer here'}
              id={!inTable ? `fpl-${ctrl.internal_id}` : `fpl-${ctrl.internal_id}-${col}-${tr}`}
            />
            {!inTable && (
              <SubtextWrapper id="descriptive text" notInBuilder={!inBuilder}>
                {ctrl.props.subtext}
              </SubtextWrapper>
            )}
          </InputWrapper>
        </Field>
      </FieldWrapper>
    </MainfieldWrapper>
  );
};

TextInput.defaultProps = {
  inTable: false,
  tr: null,
  col: null,
  formDesign: null,
};

TextInput.propTypes = {
  ctrl: PropTypes.shape(ControlPropType).isRequired,
  inTable: PropTypes.bool,
  col: PropTypes.number,
  tr: PropTypes.number,
  formDesign: PropTypes.shape({
    old: PropTypes.object.isRequired,
    design: PropTypes.object.isRequired,
  }),
};
export default TextInput;
